/**
 * Form pro A — group a flat field list into layout sections for rendering.
 *
 * The backend ships `PortalFormField[]` (in order) plus a `PortalFormSection[]`
 * and a default column count. The portal renders fields grouped under section
 * headings, each on a 1/2-column grid. This pure helper turns the flat shape
 * into ordered render groups so `PortalFormBlock` stays a thin view — and so
 * the grouping logic is unit-testable headless.
 */
import type { PortalFormField, PortalFormSection } from "@/lib/api-portal";

export interface FormGroup<F = PortalFormField> {
  /** Section id, or null for the implicit default section. */
  id: string | null;
  title: string | null;
  description: string | null;
  /** Grid columns (1 or 2). */
  columns: number;
  fields: F[];
}

/**
 * Order: the implicit default section (fields with no/unknown `section`)
 * first, then each explicit section in its declared order. Empty groups are
 * dropped, so a form with no sections collapses to a single 1-column group —
 * the legacy vertical stack. Field order within a group follows `fields`.
 */
export function buildFormGroups<F extends { section?: string | null }>(
  fields: F[],
  sections: PortalFormSection[] | undefined,
  defaultColumns: number | undefined,
): FormGroup<F>[] {
  const secs = sections ?? [];
  const known = new Set(secs.map((s) => s.id));
  const byId = new Map<string | null, F[]>();
  for (const f of fields) {
    const key = f.section && known.has(f.section) ? f.section : null;
    const bucket = byId.get(key);
    if (bucket) bucket.push(f);
    else byId.set(key, [f]);
  }

  const groups: FormGroup<F>[] = [];
  const defaults = byId.get(null);
  if (defaults && defaults.length) {
    groups.push({
      id: null,
      title: null,
      description: null,
      columns: clampColumns(defaultColumns),
      fields: defaults,
    });
  }
  for (const s of secs) {
    const f = byId.get(s.id);
    if (!f || !f.length) continue;
    groups.push({
      id: s.id,
      title: s.title ?? null,
      description: s.description ?? null,
      columns: clampColumns(s.columns),
      fields: f,
    });
  }
  return groups;
}

/** Layout columns are 1 or 2 — anything else (incl. undefined) → 1. */
export function clampColumns(n: number | undefined): number {
  return n === 2 ? 2 : 1;
}
